import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import { toastError, toastSuccess } from '@components/Toast';

import BlockDescriptionForm from '@features/blockSuggestion/components/block/BlockDescriptionForm';
import FileUploads from '@features/blockSuggestion/components/fileUpload/FileUploads';

import { fetchProductInfo } from '@store/actions/DesignToolAction';
import { closeLoader, openLoader } from '@store/actions/LoaderAction';
import { fetchProductWiseMeasurementSizeInfo } from '@store/actions/MeasurementAction';

import { copyMeasurementToAnotherProduct, fetchLatestMeasurementType } from '@services/measurementService';

import ApiClient from '@lib/apiClient';

import { changeDateFormat } from '@utils/Util';

const BlockFileList = ({
	fromPage,
	onBack,
	selectedBlockResponse,
	onAddSuccess,
	onSelectDesign,
	isEditable,
	selectedMeasurementType
}) => {
	const [isShowMeasurementTypes, setIsShowMeasurementTypes] = useState(false);
	const blockStore = useSelector((store) => store.blockStore);
	const [measurementTypes, setMeasurementTypes] = useState([]);
	const [selectedTypeId, setSelectedTypeId] = useState(null);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [isMeasurementCopied] = useState(false);
	const [isEdit, setIsEdit] = useState(false);
	const [isLoading] = useState(false);
	const dropDownRef = useRef(null);
	const dispatch = useDispatch();
	const { id } = useParams();

	useEffect(() => {
		document.addEventListener('mousedown', handleOutsideClick);
		return () => {
			document.removeEventListener('mousedown', handleOutsideClick);
		};
	}, []);

	const handleOutsideClick = (event) => {
		if (dropDownRef && !dropDownRef.current?.contains(event.target)) {
			setIsShowMeasurementTypes(false);
		}
	};

	useEffect(() => {
		if (selectedBlockResponse?.productId) {
			(async () => {
				try {
					dispatch(openLoader());
					const data = await fetchLatestMeasurementType(selectedBlockResponse.productId);
					setMeasurementTypes(data);
					dispatch(closeLoader());
				} catch {
					dispatch(closeLoader());
				}
			})();
		}
	}, [selectedBlockResponse?.productId]);

	const onSuccess = () => {
		setIsEdit(false);
		onAddSuccess();
	};

	const onAddTechPack = async () => {
		setIsShowMeasurementTypes((prev) => !prev);
	};

	const onDeleteBlock = async () => {
		setIsSubmitting(true);
		await ApiClient.POST(
			'updateProductMeasurementBlock',
			{
				isBlock: false
			},
			`${selectedBlockResponse?.id}`
		)
			.then(() => {
				onSuccess();
				setIsSubmitting(false);
			})
			.catch(({ response }) => {
				setIsSubmitting(false);
				if (response && response.data && response.data.message) {
					toastError(response.data.message);
				} else {
					toastError('Request was unsuccessful.');
				}
			});
	};

	const copyMeasurement = async (e, measurementId, sizeId) => {
		e.stopPropagation();
		try {
			dispatch(openLoader());
			const data = await copyMeasurementToAnotherProduct(
				measurementId,
				id,
				selectedMeasurementType.constant,
				sizeId
			);
			await dispatch(fetchProductWiseMeasurementSizeInfo(id, data.measurementType.constant, data.unit));
			await dispatch(fetchProductInfo(id));
			dispatch(closeLoader());
			toastSuccess('Measurement used successfully.');
		} catch {
			dispatch(closeLoader());
		}
	};

	const isBuyerAndNitexDevelopmentSelected = () => {
		return ['NITEX_DEVELOPMENT', 'BUYER_FIT_DEVELOPMENT'].includes(selectedMeasurementType.constant);
	};

	const renderTypeWiseSizes = (item) => {
		return (
			<ul className="sizes scroll-y-label">
				{item.sizeList?.map((size) => {
					return (
						<li
							className="cursor-pointer"
							key={`item_${size.id}`}
							onClick={(e) => copyMeasurement(e, item.id, size.id)}
						>
							<span className={`${size.isBaseSize ? 'semibold-14' : ''}`}>
								{size.label} {size.isBaseSize && ` (Base size)`}
							</span>
						</li>
					);
				})}
			</ul>
		);
	};

	const renderMeasurementTypes = () => {
		return (
			<div className="add-techpack-options dropdown-menu show" ref={dropDownRef}>
				<ul>
					{measurementTypes?.map((item, index) => {
						let isNitexAndBuyer = ['NITEX_DEVELOPMENT', 'BUYER_FIT_DEVELOPMENT'].includes(
							item.measurementType.constant
						);
						return (
							<li
								className="cursor-pointer"
								key={`measurement_type_${index}`}
								onClick={() => {
									setSelectedTypeId((prevId) => (prevId === item.id ? null : item.id));
								}}
							>
								{isNitexAndBuyer && (
									<span onClick={(e) => copyMeasurement(e, item.id)}>{item.name}</span>
								)}
								{!isNitexAndBuyer && (
									<>
										<span
											className="d-flex justify-content-between align-items-center"
											onClick={(e) => {
												if (!isBuyerAndNitexDevelopmentSelected()) {
													copyMeasurement(e, item.id);
												}
											}}
										>
											{item.name}
											<img
												src={`${process.env.PUBLIC_URL}/images/chevron_down.svg`}
												alt="down"
											/>
										</span>
										{isBuyerAndNitexDevelopmentSelected() && (
											<>
												{item?.sizeList?.length > 0 &&
													item.id === selectedTypeId &&
													renderTypeWiseSizes(item)}
											</>
										)}
									</>
								)}
							</li>
						);
					})}
				</ul>
			</div>
		);
	};

	return (
		<>
			{fromPage !== 'MEASUREMENT' && (
				<p className="d-flex align-items-center mb-2 cursor-pointer" onClick={onBack}>
					<img src={`${process.env.PUBLIC_URL}/icons/arrow-back.svg`} alt="back" />
					<span
						className="re
          gular-12 gray_dark_02 ml-1"
					>
						Back
					</span>
				</p>
			)}
			<div className="design-block-info d-flex">
				<div className="image mr-12">
					<img
						src={
							selectedBlockResponse?.featureImageList &&
							selectedBlockResponse?.featureImageList[0]?.docUrl
						}
						alt="design"
					/>
				</div>
				<div className="block-info">
					<h3 className="semibold-14 mb-0 hover-underline cursor-pointer" onClick={onSelectDesign}>
						{selectedBlockResponse?.productRefNo}{' '}
						<img src={`${process.env.PUBLIC_URL}/icons/arrow_forward_dark.svg`} alt />
					</h3>
					<p className="text-uppercase regular-12 gray_dark_02">
						{selectedBlockResponse?.inspirationBrand?.name}
					</p>
					{selectedBlockResponse?.isBlock && (
						<>
							<span className="status rounded-0 blue-3">Block</span>

							<p className="regular-12 gray_dark_02 mt-1">
								BY:&nbsp;
								{selectedBlockResponse?.blockCreatedBy?.nickName ||
									selectedBlockResponse?.blockCreatedBy?.name}
								,&nbsp;
								{changeDateFormat(selectedBlockResponse?.blockCreatedAt, '', 'Do MMM, YY')}
							</p>
						</>
					)}
				</div>
			</div>
			{/* Block description box */}
			<div className="block-desc-box mt-3">
				{selectedBlockResponse?.blockTitle && (
					<h5 className="semibold-14">{selectedBlockResponse?.blockTitle}</h5>
				)}
				<ul>
					{(selectedBlockResponse?.fittingTypeTag?.text ||
						selectedBlockResponse?.riseTag?.text ||
						selectedBlockResponse?.sizeCategoryResponse?.text ||
						selectedBlockResponse?.inspirationBrand?.name) && (
						<li className="regular-14">
							{selectedBlockResponse?.fittingTypeTag &&
								selectedBlockResponse?.fittingTypeTag?.text}
							{selectedBlockResponse?.riseTag?.text &&
								', ' + selectedBlockResponse?.riseTag?.text}
							{selectedBlockResponse?.sizeCategoryResponse?.text &&
								', ' + selectedBlockResponse?.sizeCategoryResponse?.text}
							{selectedBlockResponse?.inspirationBrand &&
								`, inspiration from
						${selectedBlockResponse?.inspirationBrand?.name}`}
						</li>
					)}
					{selectedBlockResponse?.blockRemarks && (
						<li className="regular-14">{selectedBlockResponse?.blockRemarks}</li>
					)}
				</ul>
				{selectedBlockResponse?.isBlock && !blockStore?.currentBlockAction && (
					<div className="d-flex justify-content-end align-items-center mt-3">
						<button className="button text-only" disabled={isSubmitting} onClick={onDeleteBlock}>
							<span className="red text-underline">DELETE BLOCK</span>
						</button>
						{!isEdit && (
							<button className="button outline size32 ml-12" onClick={() => setIsEdit(true)}>
								EDIT{' '}
							</button>
						)}
					</div>
				)}
			</div>

			{(isEdit || blockStore?.currentBlockAction) && (
				<div className="block-add-edit-box mt-3">
					<BlockDescriptionForm
						id={id}
						blockResponse={selectedBlockResponse}
						onAddSucess={onSuccess}
					/>
				</div>
			)}
			<div className="all-uploaded-files mt-12">
				{selectedBlockResponse?.hasMeasurementChart &&
					!(isEdit || blockStore?.currentBlockAction) && (
						<div className="single-type-file measurement ">
							<div className="file-type-content-box">
								<h3 className="type-title regular-12 gray_dark_02">MEASUREMENT CHART</h3>

								<div className="add-techpack">
									{!isMeasurementCopied ? (
										<button
											className="button outline size36"
											disabled={isLoading || !isEditable}
											onClick={onAddTechPack}
										>
											<div className="d-flex align-items-center ">
												<span className="gray_dark_01 mr-1">Add this techpack</span>
												<img
													src={`${process.env.PUBLIC_URL}/icons/down-arrow-gray-dark-01.svg`}
													alt="arrow"
												/>
											</div>
										</button>
									) : isMeasurementCopied ? (
										<div className="d-flex align-items-center justify-content-center">
											<img
												src={`${process.env.PUBLIC_URL}/icons/shiped.svg`}
												alt="arrow"
											/>
											<span className="green regular-14 ml-1">Added</span>
										</div>
									) : (
										''
									)}
									{isShowMeasurementTypes && isEditable && renderMeasurementTypes()}
								</div>
							</div>
						</div>
					)}
				<FileUploads id={selectedBlockResponse?.id} originFrom="BLOCK" />
			</div>
		</>
	);
};

export default BlockFileList;
